/**
 * Landing page for a Rovo Space.
 * Displays the space emoji, title, description, and tabbed space content.
 * This is a pure, stateless UI component — all data fetching and navigation
 * should be managed by the caller.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import { ResourcedEmoji } from '@atlaskit/emoji/element';
import { EmojiPicker } from '@atlaskit/emoji/picker';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading';
import PencilIcon from '@atlaskit/icon-lab/core/pencil';
import ReturnIcon from '@atlaskit/icon-lab/core/return';
import AddIcon from '@atlaskit/icon/core/add';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import DeleteIcon from '@atlaskit/icon/core/delete';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags';
import { Popup } from '@atlaskit/popup/popup';
import { Anchor, Box, Flex, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import Spinner from '@atlaskit/spinner';
import TextArea from '@atlaskit/textarea/text-area';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useEmptyChatLaunchSelector } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { ChatItem } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list-refresh/ChatItem';
import { SkillHoverCard } from '@atlassian/rovo-platform-ui-components/skills/SkillHoverCard';
import {
	SkillMetadataProvider,
	useSkillMetadata,
} from '@atlassian/rovo-skills/services/skill-metadata-provider';
import { getSkillMetadataForAction } from '@atlassian/rovo-skills/utils/get-skill-metadata-for-action';

import { useSpacesAnalytics } from '../../analytics/useSpacesAnalytics';
import { SPACE_ABOUT_MAX_LENGTH, SPACE_NAME_MAX_LENGTH } from '../../common/constants';
import { useSpaceStarters } from '../../common/starters';
import { createEmojiProvider } from '../../common/utils/EmojiProviderUtils';
import {
	useSpaceMemories,
	useSpaceMemoriesError,
	useSpaceMemoriesLoading,
	useSpaceStoreActions,
} from '../../controllers/space-store/hooks';
import type { SmartContextLinkRecommendation as RecommendedSpaceSource } from '../../controllers/space-store/types';
import { useAddSpaceSource } from '../../services/useAddSpaceSource/useAddSpaceSource';
import { usePastConversationRecommendations } from '../../services/usePastConversationRecommendations/usePastConversationRecommendations';
import { useSmartContextLinkRecommendations } from '../../services/useSmartContextLinkRecommendations/useSmartContextLinkRecommendations';
import { FolderClosedIconLarge } from '../folder-closed-icon-large';
import { PastConversationsBanner } from '../past-conversations-banner/PastConversationsBanner';
import type { RecommendationBannerVariant } from '../recommendation-banner/types';
import { SmartContextLinksBanner } from '../smart-context-links-banner/SmartContextLinksBanner';
import type {
	SmartContextLinkRecommendation as ModalLinkRecommendation,
	SmartContextRecommendation,
	SmartContextRecommendationsModalProps,
} from '../smart-context-recommendations-modal/types';

import chatAvatarsUrl from './assets/chat-avatars.svg';
import { EmptyMemoriesIcon } from './EmptyMemoriesIcon';
import { EmptySourcesState } from './EmptySourcesState';
import messages from './messages';
import { SmartLinkSourceRow } from './SmartLinkSourceRow';
import { SourceLinkPickerPopup } from './SourceLinkPickerPopup';
import type {
	SpaceChatSubmitParams,
	SpaceDetailsSaveParams,
	SpaceLandingPageProps,
	SpaceMemory,
	SpaceSource,
	SpaceStarter,
} from './types';
import { useSpaceSourcePlugins } from './useSpaceSourcePlugins';

const SmartContextRecommendationsModal = React.lazy(async () => {
	const { SmartContextRecommendationsModal } = await import(
		/* webpackChunkName: "@atlassian/rovo-spaces-smart-context-recommendations-modal" */
		'../smart-context-recommendations-modal/SmartContextRecommendationsModal'
	);

	return {
		default: (props: SmartContextRecommendationsModalProps) => (
			<SmartContextRecommendationsModal {...props} />
		),
	};
});

const styles = cssMap({
	page: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'hidden',
	},
	// Mini-modal (non-fullscreen): the parent is bounded by `maxHeight`, not a
	// definite `height`, so `height: 100%` collapses and the chat input drops
	// below the fold. Grow via `flex: 1` so `scrollArea` scrolls and the input
	// stays pinned (mirrors chat-history's `animationTransitionContainerMiniModal`).
	pageMiniModal: {
		height: 'auto',
		flex: '1',
	},
	scrollArea: {
		flex: '1',
		overflowY: 'auto',
		paddingBlockStart: token('space.500'),
		paddingBlockEnd: token('space.500'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	scrollAreaFullScreen: {
		paddingInlineStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
	},
	inner: {
		maxWidth: '800px',
		width: '100%',
		marginInlineStart: 'auto',
		marginInlineEnd: 'auto',
	},
	header: {
		marginBlockEnd: token('space.200'),
	},
	titleRow: {
		minWidth: '0px',
		flex: 1,
	},
	inlineEditReadViewButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		width: '100%',
		minWidth: '0px',
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		marginBlockStart: token('space.negative.050'),
		marginBlockEnd: token('space.negative.050'),
		marginInlineStart: token('space.negative.100'),
		marginInlineEnd: token('space.negative.100'),
		borderRadius: token('radius.small'),
		color: token('color.text'),
		backgroundColor: 'transparent',
		textAlign: 'start',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	descriptionInlineEditReadViewButton: {
		alignItems: 'start',
	},
	hiddenEditButton: {
		opacity: 0,
		transition: 'opacity 100ms ease-in-out',
	},
	visibleEditButton: {
		opacity: 1,
	},
	titleEditForm: {
		marginBlockEnd: token('space.200'),
	},
	descriptionEditForm: {
		marginBlockEnd: token('space.300'),
	},
	editField: {
		flex: 1,
		minWidth: '0px',
	},
	editControls: {
		flexShrink: 0,
	},
	editIcon: {
		flexShrink: 0,
		color: token('color.text.subtle'),
	},
	fieldError: {
		marginBlockStart: token('space.075'),
	},
	emoji: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	emojiPickerTrigger: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '40px',
		height: '40px',
		borderRadius: token('radius.small'),
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	description: {
		marginBlockEnd: token('space.300'),
	},
	tabBar: {
		display: 'inline-flex',
		gap: token('space.050'),
		marginBlockEnd: token('space.300'),
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		alignItems: 'center',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
	},
	tabButtonBase: {
		display: 'flex',
		// Column so the hidden bold copy below stacks in the block axis (height 0,
		// no vertical space) while still widening the button in the cross axis.
		flexDirection: 'column',
		minHeight: '24px',
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		font: token('font.body'),
		cursor: 'pointer',
		justifyContent: 'center',
		alignItems: 'center',
		// Active tab is semibold, which is wider than regular. Reserve the bold
		// width up front via a hidden bold copy of the label so switching tabs
		// changes weight without reflowing (and shifting) the tab row.
		'&::after': {
			content: 'attr(data-label)',
			fontWeight: token('font.weight.semibold'),
			height: 0,
			visibility: 'hidden',
			overflow: 'hidden',
			userSelect: 'none',
			pointerEvents: 'none',
		},
	},
	tabButtonActive: {
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border.selected'),
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		fontWeight: token('font.weight.semibold'),
	},
	tabButtonInactive: {
		borderStyle: 'none',
		borderWidth: 0,
		borderColor: 'transparent',
		backgroundColor: 'transparent',
		color: token('color.text.subtle'),
		fontWeight: token('font.weight.regular'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text'),
		},
	},
	sourceList: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		alignSelf: 'stretch',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		backgroundColor: token('elevation.surface'),
	},
	emptySourcesCard: {
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.600'),
		paddingInlineStart: token('space.600'),
		paddingInlineEnd: token('space.600'),
		borderRadius: token('radius.xlarge'),
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		alignSelf: 'stretch',
	},
	starterList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	starterCard: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.150'),
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		paddingInlineStart: token('space.075'),
		paddingInlineEnd: token('space.075'),
		alignSelf: 'stretch',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		cursor: 'pointer',
		width: '100%',
		textAlign: 'start',
		border: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025', '2px'),
		},
	},
	starterIconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		borderRadius: token('radius.tile'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		flexShrink: 0,
	},
	starterText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
	},
	chatList: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
	},
	emptyChatsCard: {
		width: '100%',
		minHeight: '240px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.600'),
		paddingInlineStart: token('space.600'),
		paddingInlineEnd: token('space.600'),
		borderRadius: token('radius.xlarge'),
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
	},
	emptyChatsIcon: {
		width: '100px',
		height: '100px',
		flexShrink: 0,
	},
	emptyChatsText: {
		width: '100%',
		maxWidth: '476px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.050'),
		textAlign: 'center',
	},
	chatsLoading: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.600'),
	},
	sourceCard: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.100'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
		borderBlockEndColor: token('color.border'),
	},
	noBorderEnd: {
		borderBlockEndWidth: 0,
		borderBlockEndStyle: 'none',
		borderBlockEndColor: 'transparent',
	},
	sourceLink: {
		display: 'flex',
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: '0px',
		alignItems: 'center',
		minWidth: '0px',
		overflow: 'hidden',
		textDecoration: 'none',
		color: token('color.text'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	deleteButtonWrapper: {
		marginInlineStart: 'auto',
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		paddingInlineStart: token('space.075'),
		paddingInlineEnd: token('space.075'),
		borderRadius: token('radius.small'),
		color: token('color.text.subtle'),
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
			color: token('color.text.danger'),
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	chatInputArea: {
		flexShrink: 0,
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		backgroundColor: token('elevation.surface'),
	},
	chatInputAreaFullScreen: {
		paddingInlineStart: token('space.400'),
		paddingInlineEnd: token('space.400'),
	},
	chatInputInner: {
		maxWidth: '800px',
		width: '100%',
		marginInlineStart: 'auto',
		marginInlineEnd: 'auto',
	},
	spaceContextBannerWrapper: {
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	aiDisclaimer: {
		textAlign: 'center',
		marginBlockStart: token('space.100'),
	},
	aiDisclaimerAnchor: {
		textDecoration: 'none',
		color: token('color.text.subtlest'),
		'&:hover': {
			textDecoration: 'underline',
			color: token('color.text.subtlest'),
		},
	},
	emptyMemoriesCard: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.150'),
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.600'),
		paddingInlineStart: token('space.600'),
		paddingInlineEnd: token('space.600'),
		borderRadius: token('radius.xlarge'),
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
	},
	emptyMemoriesText: {
		textAlign: 'center',
	},
});

type StarterCardProps = {
	starter: SpaceStarter;
	onClick: (starter: SpaceStarter) => void;
	icon?: React.ReactNode;
	title?: string;
};

const StarterCard = ({ starter, onClick, icon, title }: StarterCardProps): React.JSX.Element => {
	// Look up skill metadata for the starter's slash command to render SkillHoverCard
	const skillMetadata = useSkillMetadata();
	const skillMetadataMap = skillMetadata?.skillMetadataMap;
	const metadata = getSkillMetadataForAction(starter, skillMetadataMap);
	const card = (
		<Pressable
			xcss={styles.starterCard}
			onClick={() => onClick(starter)}
			testId="space-starter-card"
		>
			{icon && (
				<Box xcss={styles.starterIconWrapper} aria-hidden="true">
					{icon}
				</Box>
			)}
			<Box xcss={styles.starterText}>{title ?? starter.text}</Box>
		</Pressable>
	);

	if (!metadata) {
		return card;
	}

	return (
		<SkillHoverCard
			name={metadata.name}
			iconName={metadata.iconName}
			description={metadata.description ?? ''}
			product={metadata.product}
			productIconUrl={metadata.productIconUrl}
			shouldRenderToParent
		>
			{card}
		</SkillHoverCard>
	);
};

type SourceCardProps = {
	source: SpaceSource;
	onDelete?: (source: SpaceSource) => void;
	deleteLabel: string;
	isLast: boolean;
};

const SourceCard = ({
	source,
	onDelete,
	deleteLabel,
	isLast,
}: SourceCardProps): React.JSX.Element => {
	const [isLoading, setIsLoading] = useState(true);

	return (
		<Box
			xcss={cx(styles.sourceCard, isLast ? styles.noBorderEnd : undefined)}
			testId="space-source-item"
		>
			<Anchor href={source.url} target="_blank" xcss={styles.sourceLink}>
				<SmartLinkSourceRow
					url={source.url}
					fallback={source.title}
					displayTextOverride={source.title !== source.url ? source.title : undefined}
					onLoadingChange={setIsLoading}
				/>
			</Anchor>
			{onDelete && !isLoading && (
				<Pressable
					xcss={styles.deleteButtonWrapper}
					onClick={() => onDelete(source)}
					testId="source-delete-button"
					aria-label={deleteLabel}
				>
					<DeleteIcon label="" />
				</Pressable>
			)}
		</Box>
	);
};

type MemoryCardProps = {
	memory: SpaceMemory;
	onDelete?: (memory: SpaceMemory) => void;
	deleteLabel: string;
	isLast: boolean;
};

const MemoryCard = ({
	memory,
	onDelete,
	deleteLabel,
	isLast,
}: MemoryCardProps): React.JSX.Element => (
	<Box
		xcss={cx(styles.sourceCard, isLast ? styles.noBorderEnd : undefined)}
		testId="space-memory-item"
	>
		<Text>{memory.text}</Text>
		{onDelete && (
			<Pressable
				xcss={styles.deleteButtonWrapper}
				onClick={() => onDelete(memory)}
				testId="memory-delete-button"
				aria-label={deleteLabel}
			>
				<DeleteIcon label="" />
			</Pressable>
		)}
	</Box>
);

type PastConversationsBannerSlotProps = {
	spaceId: string;
	variant: RecommendationBannerVariant;
	onDismissFocusTarget?: () => void;
};

type DismissibleRecommendationBannerSlotProps = {
	shouldRender: boolean;
	onDismissFocusTarget?: () => void;
	children: (slotActions: { dismiss: () => void }) => React.ReactNode;
};

const chatInputFocusableSelector = [
	'button:not([disabled])',
	'a[href]',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

const toModalLinkRecommendation = (
	recommendation: RecommendedSpaceSource,
): ModalLinkRecommendation => ({
	type: 'link',
	recommendationId: recommendation.recommendationId,
	url: recommendation.url,
	ari: recommendation.ari,
	sourceType: recommendation.sourceType,
	name: recommendation.name,
});

const getErrorStatusCode = (error: unknown): number | undefined => {
	if (error && typeof error === 'object' && 'errorAttributes' in error) {
		const { errorAttributes } = error as { errorAttributes?: { statusCode?: number } };
		return errorAttributes?.statusCode;
	}

	return undefined;
};

const isLinkRecommendation = (
	recommendation: SmartContextRecommendation,
): recommendation is ModalLinkRecommendation => recommendation.type === 'link';

const DismissibleRecommendationBannerSlot = ({
	shouldRender,
	onDismissFocusTarget,
	children,
}: DismissibleRecommendationBannerSlotProps): React.JSX.Element | null => {
	const [isDismissed, setIsDismissed] = useState(false);

	const dismiss = useCallback(() => {
		onDismissFocusTarget?.();
		setIsDismissed(true);
	}, [onDismissFocusTarget]);

	if (!shouldRender || isDismissed) {
		return null;
	}

	return <Box xcss={styles.spaceContextBannerWrapper}>{children({ dismiss })}</Box>;
};

/**
 * Wraps the {@link PastConversationsBanner} together with its data-fetching
 * hook so that the recommendations query is *only* executed when this slot is
 * actually rendered.
 *
 * The slot also owns its own visual wrapper (`spaceContextBannerWrapper`) so
 * that when the hook returns `count <= 0` the *entire* DOM subtree (wrapper
 * `<Box>` included) is removed \u2014 not just the inner banner. This avoids
 * leaving an empty padded `<Box>` in the layout.
 *
 * Critical: the parent must render `<PastConversationsBannerSlot />` inside
 * the spaces and smart-context feature flag guards. Today the stub hook does
 * no I/O so either rendering pattern would work, but once the hook is wired to
 * a real backend call in a follow-up ticket, gating the slot here prevents
 * requests being fired for users without the gates.
 */
const PastConversationsBannerSlot = ({
	spaceId,
	variant,
	onDismissFocusTarget,
}: PastConversationsBannerSlotProps): React.JSX.Element | null => {
	const { count: pastConversationsCount } = usePastConversationRecommendations({
		spaceId,
	});

	return (
		<DismissibleRecommendationBannerSlot
			shouldRender={pastConversationsCount > 0}
			onDismissFocusTarget={onDismissFocusTarget}
		>
			{({ dismiss }) => (
				<PastConversationsBanner
					conversationCount={pastConversationsCount}
					onAddChats={() => {}}
					onDismiss={dismiss}
					variant={variant}
				/>
			)}
		</DismissibleRecommendationBannerSlot>
	);
};

type SmartContextLinksBannerSlotProps = {
	spaceId: string;
	cloudId: string;
	recommendations: RecommendedSpaceSource[];
	variant: RecommendationBannerVariant;
	onDismissFocusTarget?: () => void;
};

type SmartContextLinksBannerContentProps = Omit<
	SmartContextLinksBannerSlotProps,
	'onDismissFocusTarget'
> & {
	dismiss: () => void;
};

const SmartContextLinksBannerContent = ({
	spaceId,
	cloudId,
	recommendations,
	variant,
	dismiss,
}: SmartContextLinksBannerContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { addSource, isAdding } = useAddSpaceSource({ spaceId, cloudId });
	const { removeSmartContextLinkRecommendation, clearSmartContextLinkRecommendations } =
		useSpaceStoreActions();
	const { fireSourceAdded, fireSourceRecommendationsViewed } = useSpacesAnalytics();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isBatchAdding, setIsBatchAdding] = useState(false);
	const [selectedRecommendationIds, setSelectedRecommendationIds] = useState<string[]>([]);
	const [completedRecommendationIds, setCompletedRecommendationIds] = useState<string[]>([]);
	const [submitErrorMessage, setSubmitErrorMessage] = useState<string | undefined>();

	// This content only renders when there are recommendations (the slot guards
	// on `recommendations.length > 0`), so mount == the recommendations were
	// shown. Fires the impression that forms the CTR denominator.
	useEffect(() => {
		fireSourceRecommendationsViewed({ spaceId, recommendationCount: recommendations.length });
		// Impression fires once when the recommendations banner is shown.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const modalRecommendations = useMemo(
		() => recommendations.map(toModalLinkRecommendation),
		[recommendations],
	);

	const openModal = useCallback(() => {
		const completedIds = new Set(completedRecommendationIds);

		setSubmitErrorMessage(undefined);
		setSelectedRecommendationIds(
			recommendations
				.filter((recommendation) => !completedIds.has(recommendation.recommendationId))
				.map((recommendation) => recommendation.recommendationId),
		);
		setIsModalOpen(true);
	}, [completedRecommendationIds, recommendations]);

	const closeModal = useCallback(() => {
		setSubmitErrorMessage(undefined);
		setIsModalOpen(false);
	}, []);

	const markRecommendationCompleted = useCallback((recommendationId: string) => {
		setCompletedRecommendationIds((currentIds) =>
			currentIds.includes(recommendationId) ? currentIds : [...currentIds, recommendationId],
		);
		setSelectedRecommendationIds((currentIds) =>
			currentIds.filter((id) => id !== recommendationId),
		);
	}, []);

	const dismissRecommendations = useCallback(() => {
		clearSmartContextLinkRecommendations(spaceId);
		dismiss();
	}, [clearSmartContextLinkRecommendations, dismiss, spaceId]);

	const handleSubmit = useCallback(
		async (selectedRecommendations: SmartContextRecommendation[]) => {
			const selectedLinks = selectedRecommendations.filter(isLinkRecommendation);

			let didNotFoundError = false;
			let didNonNotFoundError = false;
			// Track the items actually added to the space so we can drop exactly those
			// from the store, even if the batch fails partway through.
			const addedRecommendationIds: string[] = [];
			setSubmitErrorMessage(undefined);
			setIsBatchAdding(true);

			try {
				for (const recommendation of selectedLinks) {
					let addSourceError: unknown;

					await addSource(
						{
							ari: recommendation.ari,
							url: recommendation.url,
							sourceType: recommendation.sourceType,
							name: recommendation.name,
						},
						{
							onError: (error) => {
								addSourceError = error;
							},
						},
					);

					if (addSourceError) {
						const statusCode = getErrorStatusCode(addSourceError);

						if (statusCode === 404) {
							didNotFoundError = true;
							markRecommendationCompleted(recommendation.recommendationId);
						} else {
							didNonNotFoundError = true;
						}
					} else {
						fireSourceAdded({
							spaceId,
							sourceType: recommendation.sourceType,
							addMethod: 'recommendation',
							recommendationId: recommendation.recommendationId,
						});
						markRecommendationCompleted(recommendation.recommendationId);
						addedRecommendationIds.push(recommendation.recommendationId);
					}
				}
			} finally {
				setIsBatchAdding(false);
			}

			// Always remove the recommendations that were actually added to the space so a
			// retry can't re-submit them as duplicate sources, even on a partial failure.
			addedRecommendationIds.forEach((recommendationId) => {
				removeSmartContextLinkRecommendation(spaceId, recommendationId);
			});

			if (didNonNotFoundError) {
				setSubmitErrorMessage(formatMessage(messages.addLinksInvalidDataError));
				return;
			}

			if (didNotFoundError || selectedLinks.length === 0) {
				setIsModalOpen(false);
				clearSmartContextLinkRecommendations(spaceId);
				dismiss();
				return;
			}

			setIsModalOpen(false);
			dismiss();
		},
		[
			addSource,
			clearSmartContextLinkRecommendations,
			dismiss,
			formatMessage,
			markRecommendationCompleted,
			removeSmartContextLinkRecommendation,
			spaceId,
			fireSourceAdded,
		],
	);

	const submitSelectedRecommendations = useCallback(
		(selectedRecommendations: SmartContextRecommendation[]) => {
			handleSubmit(selectedRecommendations).catch(() => {
				setIsBatchAdding(false);
				setSubmitErrorMessage(formatMessage(messages.addLinksInvalidDataError));
			});
		},
		[formatMessage, handleSubmit],
	);

	return (
		<>
			<SmartContextLinksBanner
				recommendationCount={recommendations.length}
				onAddLinks={openModal}
				onDismiss={dismissRecommendations}
				variant={variant}
			/>
			{isModalOpen && (
				<React.Suspense fallback={null}>
					<SmartContextRecommendationsModal
						isOpen={isModalOpen}
						recommendationType="link"
						recommendations={modalRecommendations}
						selectedRecommendationIds={selectedRecommendationIds}
						onSelectionChange={setSelectedRecommendationIds}
						onClose={closeModal}
						onSubmit={submitSelectedRecommendations}
						isSubmitting={isAdding || isBatchAdding}
						errorMessage={submitErrorMessage}
					/>
				</React.Suspense>
			)}
		</>
	);
};

const SmartContextLinksBannerSlot = ({
	spaceId,
	cloudId,
	recommendations,
	variant,
	onDismissFocusTarget,
}: SmartContextLinksBannerSlotProps): React.JSX.Element | null => {
	return (
		<DismissibleRecommendationBannerSlot
			shouldRender={recommendations.length > 0}
			onDismissFocusTarget={onDismissFocusTarget}
		>
			{({ dismiss }) => (
				<SmartContextLinksBannerContent
					spaceId={spaceId}
					cloudId={cloudId}
					recommendations={recommendations}
					variant={variant}
					dismiss={dismiss}
				/>
			)}
		</DismissibleRecommendationBannerSlot>
	);
};

type SmartContextRecommendationsBannerSlotProps = {
	spaceId: string;
	cloudId: string;
	variant: RecommendationBannerVariant;
	onDismissFocusTarget?: () => void;
};

const SmartContextRecommendationsBannerSlot = ({
	spaceId,
	cloudId,
	variant,
	onDismissFocusTarget,
}: SmartContextRecommendationsBannerSlotProps): React.JSX.Element | null => {
	const { clearSmartContextLinkRecommendations } = useSpaceStoreActions();
	const { recommendations: linkRecommendations, count: linkRecommendationsCount } =
		useSmartContextLinkRecommendations({ spaceId });

	// Clear this space's stream-pushed recommendations when leaving it. The slot is
	// keyed by spaceId, so this fires on space change/unmount and stops the store from
	// accumulating stale recommendations across visited spaces.
	useEffect(() => {
		return () => {
			clearSmartContextLinkRecommendations(spaceId);
		};
	}, [clearSmartContextLinkRecommendations, spaceId]);

	if (linkRecommendationsCount > 0) {
		return (
			<SmartContextLinksBannerSlot
				spaceId={spaceId}
				cloudId={cloudId}
				recommendations={linkRecommendations}
				variant={variant}
				onDismissFocusTarget={onDismissFocusTarget}
			/>
		);
	}

	return (
		<PastConversationsBannerSlot
			spaceId={spaceId}
			variant={variant}
			onDismissFocusTarget={onDismissFocusTarget}
		/>
	);
};

type Tab = 'starters' | 'chats' | 'sources' | 'memory';
type EditingSpaceField = 'title' | 'description' | null;

type InlineEditActionButtonsProps = {
	saveLabel: string;
	cancelLabel: string;
	isSaving: boolean;
	onCancel: () => void;
};

const InlineEditActionButtons = ({
	saveLabel,
	cancelLabel,
	isSaving,
	onCancel,
}: InlineEditActionButtonsProps): React.JSX.Element => {
	const preventFocusSteal = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	}, []);

	return (
		<Inline xcss={styles.editControls} space="space.050" alignBlock="center">
			<IconButton
				type="submit"
				icon={CheckMarkIcon}
				appearance="primary"
				spacing="compact"
				label={saveLabel}
				isLoading={isSaving}
				isDisabled={isSaving}
				onMouseDown={preventFocusSteal}
				testId="space-details-save-button"
			/>
			<IconButton
				type="button"
				icon={CrossIcon}
				appearance="subtle"
				spacing="compact"
				label={cancelLabel}
				isDisabled={isSaving}
				onClick={onCancel}
				onMouseDown={preventFocusSteal}
				testId="space-details-cancel-button"
			/>
		</Inline>
	);
};

type TabButtonProps = {
	label: string;
	isActive: boolean;
	testId: string;
	onClick: () => void;
};

const TabButton = ({ label, isActive, testId, onClick }: TabButtonProps) => (
	<Pressable
		aria-selected={isActive}
		testId={testId}
		onClick={onClick}
		role="tab"
		data-label={label}
		xcss={cx(styles.tabButtonBase, isActive ? styles.tabButtonActive : styles.tabButtonInactive)}
	>
		{label}
	</Pressable>
);

const SpaceLandingPageComponent = ({
	spaceId,
	cloudId,
	emoji,
	title,
	description,
	isDescriptionLoading = false,
	starters,
	sources,
	chats = [],
	isChatsLoading = false,
	onStarterClick,
	onChatClick,
	onChatSubmit,
	onSpaceDetailsSave,
	renderChatInput,
	onDeleteSource,
	onSourceAdded,
	emojiProvider: emojiProviderProp,
	fullScreen = false,
	entryPoint,
}: SpaceLandingPageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [activeTab, setActiveTab] = useState<Tab>('starters');
	const spaceStarters = useSpaceStarters({ spaceName: title });
	const isSpacesEnabled = FeatureGates.getExperimentValue(
		'rovo_chat_spaces_experiment',
		'isEnabled',
		false,
	);
	const isSmartContextRecommendationsEnabled = fg('rovo_chat_spaces_smart_context_recommendations');
	// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- This banner is part of Spaces but also has its own smart-context rollout gate; remove the layering when either rollout gate is cleaned up.
	const shouldRenderSmartContextRecommendations =
		isSpacesEnabled && isSmartContextRecommendationsEnabled;

	// Reset to the default tab whenever the user navigates to a different space
	useEffect(() => {
		setActiveTab('starters');
	}, [spaceId]);

	// Fire a screen-view event per space viewed (adoption / return-visit metric).
	// Keyed on spaceId so navigating between spaces logs a distinct view; source
	// and chat counts are best-effort at view time.
	const {
		fireSpaceViewed,
		fireSourceAdded,
		fireSourceRemoved,
		fireStarterClicked,
		fireMemoryCreated,
		fireMemoriesViewed,
	} = useSpacesAnalytics();
	useEffect(() => {
		// NOTE: `sources` and `chats` are fetched asynchronously by the host and
		// are typically still empty at the moment `spaceId` changes, so their
		// lengths would be a misleading `0` on every navigation. Counts are
		// intentionally omitted here — the accurate per-space totals are captured
		// on the `created`/`added` events instead.
		fireSpaceViewed({
			spaceId,
			fullScreen,
			entryPoint,
		});
		// Intentionally fire only when the viewed space changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [spaceId]);

	const plugins = useSpaceSourcePlugins(cloudId);

	const [editingSpaceField, setEditingSpaceField] = useState<EditingSpaceField>(null);
	const [draftTitle, setDraftTitle] = useState(title);
	const [draftDescription, setDraftDescription] = useState(description ?? '');
	const [spaceDetailsError, setSpaceDetailsError] = useState<string | null>(null);
	const [isSavingSpaceDetails, setIsSavingSpaceDetails] = useState(false);
	const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
	const [isTitleEditButtonVisible, setIsTitleEditButtonVisible] = useState(false);
	const [isDescriptionEditButtonVisible, setIsDescriptionEditButtonVisible] = useState(false);
	const spaceDetailsErrorId =
		spaceDetailsError && editingSpaceField
			? `space-${editingSpaceField}-inline-edit-error`
			: undefined;

	useEffect(() => {
		if (!editingSpaceField) {
			setDraftTitle(title);
		}
	}, [editingSpaceField, title]);

	useEffect(() => {
		if (!editingSpaceField) {
			setDraftDescription(description ?? '');
		}
	}, [description, editingSpaceField]);

	const startEditingTitle = useCallback(() => {
		setDraftTitle(title);
		setSpaceDetailsError(null);
		setEditingSpaceField('title');
	}, [title]);

	const startEditingDescription = useCallback(() => {
		setDraftDescription(description ?? '');
		setSpaceDetailsError(null);
		setEditingSpaceField('description');
	}, [description]);

	const cancelEditingSpaceDetails = useCallback(() => {
		setDraftTitle(title);
		setDraftDescription(description ?? '');
		setSpaceDetailsError(null);
		setEditingSpaceField(null);
	}, [description, title]);

	const closeEmojiPicker = useCallback(() => {
		setIsEmojiPickerOpen(false);
	}, []);

	const toggleEmojiPicker = useCallback(() => {
		setIsEmojiPickerOpen((isOpen) => !isOpen);
	}, []);

	const hideTitleEditButtonOnBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			setIsTitleEditButtonVisible(false);
		}
	}, []);

	const hideDescriptionEditButtonOnBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			setIsDescriptionEditButtonVisible(false);
		}
	}, []);

	const saveSpaceDetails = useCallback(
		async (event?: React.FormEvent<HTMLFormElement>) => {
			event?.preventDefault();
			if (!onSpaceDetailsSave || isSavingSpaceDetails) {
				return;
			}

			const nextDetails: SpaceDetailsSaveParams = {
				title: draftTitle.trim(),
				description: draftDescription.trim() || undefined,
				emoji,
			};

			if (!nextDetails.title) {
				setSpaceDetailsError(formatMessage(messages.titleRequired));
				return;
			}

			setSpaceDetailsError(null);
			setIsSavingSpaceDetails(true);
			try {
				await onSpaceDetailsSave(nextDetails);
				setEditingSpaceField(null);
			} catch {
				setSpaceDetailsError(formatMessage(messages.saveDetailsError));
			} finally {
				setIsSavingSpaceDetails(false);
			}
		},
		[draftDescription, draftTitle, emoji, formatMessage, isSavingSpaceDetails, onSpaceDetailsSave],
	);

	const saveSpaceEmoji = useCallback<
		NonNullable<React.ComponentProps<typeof EmojiPicker>['onSelection']>
	>(
		async (selectedEmojiId) => {
			if (!onSpaceDetailsSave || isSavingSpaceDetails) {
				return;
			}

			setSpaceDetailsError(null);
			setIsSavingSpaceDetails(true);

			try {
				await onSpaceDetailsSave({
					title,
					description,
					emoji: selectedEmojiId.id,
				});
				setIsEmojiPickerOpen(false);
			} catch {
				setSpaceDetailsError(formatMessage(messages.saveDetailsError));
			} finally {
				setIsSavingSpaceDetails(false);
			}
		},
		[description, formatMessage, isSavingSpaceDetails, onSpaceDetailsSave, title],
	);

	// Memory store state and actions.
	const storeActions = useSpaceStoreActions();
	const memories = useSpaceMemories(spaceId);
	const isMemoriesLoading = useSpaceMemoriesLoading(spaceId);
	const memoriesStoreError = useSpaceMemoriesError(spaceId);

	const [memoryDeleteErrorSpaceId, setMemoryDeleteErrorSpaceId] = useState<string | null>(null);
	useEffect(() => {
		setMemoryDeleteErrorSpaceId(null);
	}, [spaceId]);

	const handleDeleteMemory = useCallback(
		async (memory: SpaceMemory) => {
			setMemoryDeleteErrorSpaceId(null);
			try {
				await storeActions.removeSpaceMemory(spaceId, memory.id, cloudId, 'rovo', 'ai-mate');
			} catch {
				setMemoryDeleteErrorSpaceId(spaceId);
			}
		},
		[storeActions, spaceId, cloudId],
	);

	// Lazily fetch memories when the memory tab is first opened.
	// fetchSpaceMemories is idempotent — it skips if already loading or fetched.
	useEffect(() => {
		if (activeTab === 'memory') {
			void storeActions.fetchSpaceMemories({
				spaceId,
				cloudId,
				product: 'rovo',
				experienceId: 'ai-mate',
			});
		}
	}, [activeTab, spaceId, cloudId, storeActions]);

	// Fire a "memories retrieved" signal once per space when the memory tab is
	// opened and the load settles (retrieval metric — memories shown per space).
	const memoriesViewedForSpaceRef = useRef<string | null>(null);
	useEffect(() => {
		if (activeTab !== 'memory' || isMemoriesLoading) {
			return;
		}
		if (memoriesViewedForSpaceRef.current === spaceId) {
			return;
		}
		memoriesViewedForSpaceRef.current = spaceId;
		fireMemoriesViewed({ spaceId, count: memories.length });
	}, [activeTab, isMemoriesLoading, spaceId, memories.length, fireMemoriesViewed]);

	// Manual source add via the Sources-tab link picker. Fires the analytics
	// event with `addMethod: 'manual'` (vs the recommendation path) then forwards
	// to the host-provided `onSourceAdded` which performs the persistence.
	const handleManualSourceAdded = useCallback(
		(source: SpaceSource) => {
			fireSourceAdded({ spaceId, sourceType: source.sourceType, addMethod: 'manual' });
			onSourceAdded?.(source);
		},
		[fireSourceAdded, spaceId, onSourceAdded],
	);

	const zeroSourcesBannerAction = useMemo(
		() =>
			onSourceAdded && sources.length === 0 ? (
				<SourceLinkPickerPopup
					plugins={plugins}
					cloudId={cloudId}
					sources={sources}
					onSourceAdded={handleManualSourceAdded}
					trigger={({ ref, onClick, ...triggerProps }) => (
						<span ref={ref} {...triggerProps}>
							<Button
								appearance="primary"
								onClick={onClick}
								testId="zero-sources-banner-add-sources-button"
								shouldFitContainer
							>
								{formatMessage(messages.addSourcesButton)}
							</Button>
						</span>
					)}
				/>
			) : undefined,
		[onSourceAdded, sources, plugins, cloudId, handleManualSourceAdded, formatMessage],
	);

	// Fire the source-removed analytics event, then forward to the host-provided
	// `onDeleteSource` which performs the removal. Only defined when the host
	// supports deletion, so the delete affordance stays gated on `onDeleteSource`.
	const handleDeleteSource = useMemo(
		() =>
			onDeleteSource
				? (source: SpaceSource) => {
						fireSourceRemoved({ spaceId, sourceType: source.sourceType });
						onDeleteSource(source);
					}
				: undefined,
		[fireSourceRemoved, spaceId, onDeleteSource],
	);

	// Fire the starter-clicked analytics event (which starter drove engagement),
	// then forward to the host-provided `onStarterClick`.
	const handleStarterClick = useCallback(
		(starter: SpaceStarter) => {
			fireStarterClicked({ spaceId, starterId: starter.id });
			onStarterClick(starter);
		},
		[fireStarterClicked, spaceId, onStarterClick],
	);

	const [emptyChatLaunch] = useEmptyChatLaunchSelector();
	// Memoize to isolate re-renders to `skillRegistryTags` changes rather than any
	// change on the empty-chat-launch slice (customGreeting, projectContext, etc.).
	const skillRegistryTags = useMemo(
		() => emptyChatLaunch.skillRegistryTags,
		[emptyChatLaunch.skillRegistryTags],
	);

	// Memoized so `action.icon?.()` (and the whole `.map`) only runs when the
	// starters tab is active AND the starter list actually changes — not on
	// every render (which would defeat the icon-lazy pattern). Returns `null`
	// when a different tab is active so the JSX is never constructed.
	const starterCardList = useMemo(
		() =>
			activeTab === 'starters' ? (
				<Box xcss={styles.starterList} role="tabpanel" testId="starters-list">
					{spaceStarters.map((action) => (
						<StarterCard
							key={action.id}
							starter={{
								id: action.id,
								text: action.staticPrompt ?? '',
								handleType: action.handleType,
								staticPrompt: action.staticPrompt,
							}}
							onClick={handleStarterClick}
							icon={action.icon?.()}
							title={action.title}
						/>
					))}
				</Box>
			) : null,
		[activeTab, spaceStarters, handleStarterClick],
	);

	const starterCardWithSkillTags =
		skillRegistryTags && skillRegistryTags.length > 0 && !fullScreen ? (
			<SkillMetadataProvider siteId={cloudId} tags={{ skillRegistryTags }}>
				{starterCardList}
			</SkillMetadataProvider>
		) : (
			starterCardList
		);

	// Inline "create memory" input state.
	const [isAddingMemory, setIsAddingMemory] = useState(false);
	const [newMemoryText, setNewMemoryText] = useState('');
	const [memorySubmitError, setMemorySubmitError] = useState<Error | null>(null);
	const [isSubmittingMemory, setIsSubmittingMemory] = useState(false);

	const openAddMemory = useCallback(() => {
		setNewMemoryText('');
		setMemorySubmitError(null);
		setIsAddingMemory(true);
	}, []);

	const handleSubmitMemory = useCallback(async () => {
		const text = newMemoryText.trim();
		if (!text || isSubmittingMemory) {
			return;
		}
		setMemorySubmitError(null);
		setIsSubmittingMemory(true);
		try {
			await storeActions.createSpaceMemory({
				spaceId,
				cloudId,
				content: text,
				product: 'rovo',
				experienceId: 'ai-mate',
			});
			fireMemoryCreated({ spaceId });
			// Only clear the input after the store action succeeds so the user
			// can retry if something throws.
			setIsAddingMemory(false);
			setNewMemoryText('');
		} catch (error) {
			setMemorySubmitError(error instanceof Error ? error : new Error(String(error)));
		} finally {
			setIsSubmittingMemory(false);
		}
	}, [storeActions, spaceId, cloudId, newMemoryText, isSubmittingMemory, fireMemoryCreated]);

	const emojiProvider = useMemo(
		() => emojiProviderProp ?? createEmojiProvider(cloudId),
		[emojiProviderProp, cloudId],
	);

	const spaceEmoji = emoji ? (
		<ResourcedEmoji
			emojiId={{ id: emoji, shortName: '' }}
			emojiProvider={emojiProvider}
			fitToHeight={24}
		/>
	) : (
		<FolderClosedIconLarge color={token('color.text.subtlest')} />
	);

	const spaceEmojiTrigger = onSpaceDetailsSave ? (
		<Popup
			isOpen={isEmojiPickerOpen}
			onClose={closeEmojiPicker}
			placement="bottom-start"
			content={() => <EmojiPicker emojiProvider={emojiProvider} onSelection={saveSpaceEmoji} />}
			trigger={(triggerProps) => (
				<Pressable
					{...triggerProps}
					aria-label={formatMessage(messages.changeEmojiLabel)}
					isDisabled={isSavingSpaceDetails}
					onClick={toggleEmojiPicker}
					testId="space-emoji-picker-trigger"
					xcss={styles.emojiPickerTrigger}
				>
					{spaceEmoji}
				</Pressable>
			)}
		/>
	) : (
		<Box xcss={styles.emoji} aria-hidden="true">
			{spaceEmoji}
		</Box>
	);

	// Stable submit handler forwarded to the host-provided chat input render-prop.
	const handleSubmit = useCallback(
		(params: SpaceChatSubmitParams) => {
			onChatSubmit?.(params);
		},
		[onChatSubmit],
	);
	const chatInputFocusTargetRef = useRef<HTMLDivElement>(null);
	const focusChatInput = useCallback(() => {
		chatInputFocusTargetRef.current
			?.querySelector<HTMLElement>(chatInputFocusableSelector)
			?.focus();
	}, []);

	return (
		<Box xcss={cx(styles.page, !fullScreen && styles.pageMiniModal)} testId="space-landing-page">
			<Box xcss={cx(styles.scrollArea, fullScreen && styles.scrollAreaFullScreen)}>
				<Box xcss={styles.inner}>
					{editingSpaceField === 'title' && onSpaceDetailsSave ? (
						<form onSubmit={saveSpaceDetails}>
							<Box xcss={styles.titleEditForm}>
								<Flex alignItems="center" gap="space.200">
									{spaceEmojiTrigger}
									<Box xcss={styles.editField}>
										<Flex alignItems="center" gap="space.100">
											<Box xcss={styles.editField}>
												<Textfield
													aria-label={formatMessage(messages.titleInputLabel)}
													aria-describedby={spaceDetailsErrorId}
													autoFocus
													maxLength={SPACE_NAME_MAX_LENGTH}
													value={draftTitle}
													onChange={(event) => setDraftTitle(event.currentTarget.value)}
													onKeyDown={(event) => {
														if (event.key === 'Escape') {
															cancelEditingSpaceDetails();
														}
													}}
												/>
											</Box>
											<InlineEditActionButtons
												saveLabel={formatMessage(messages.saveTitleButton)}
												cancelLabel={formatMessage(messages.cancelTitleButton)}
												isSaving={isSavingSpaceDetails}
												onCancel={cancelEditingSpaceDetails}
											/>
										</Flex>
										{spaceDetailsError && (
											<Box xcss={styles.fieldError}>
												<div id={spaceDetailsErrorId} role="alert">
													<Text color="color.text.danger">{spaceDetailsError}</Text>
												</div>
											</Box>
										)}
									</Box>
								</Flex>
							</Box>
						</form>
					) : (
						<Flex alignItems="center" xcss={styles.header} gap="space.200">
							{spaceEmojiTrigger}
							{onSpaceDetailsSave ? (
								<Pressable
									xcss={styles.inlineEditReadViewButton}
									aria-label={formatMessage(messages.editTitleLabel)}
									onClick={startEditingTitle}
									onMouseEnter={() => setIsTitleEditButtonVisible(true)}
									onMouseLeave={() => setIsTitleEditButtonVisible(false)}
									onFocus={() => setIsTitleEditButtonVisible(true)}
									onBlur={hideTitleEditButtonOnBlur}
									testId="space-title-edit-button"
								>
									<Box xcss={styles.titleRow}>
										<Heading size="large">{title}</Heading>
									</Box>
									<Box
										xcss={cx(
											styles.editIcon,
											styles.hiddenEditButton,
											isTitleEditButtonVisible && styles.visibleEditButton,
										)}
										aria-hidden="true"
									>
										<PencilIcon label="" />
									</Box>
								</Pressable>
							) : (
								<Box xcss={styles.titleRow}>
									<Heading size="large">{title}</Heading>
								</Box>
							)}
						</Flex>
					)}

					{spaceDetailsError && !editingSpaceField && (
						<Box xcss={styles.fieldError}>
							<div role="alert">
								<Text color="color.text.danger">{spaceDetailsError}</Text>
							</div>
						</Box>
					)}

					{editingSpaceField === 'description' && onSpaceDetailsSave ? (
						<form onSubmit={saveSpaceDetails}>
							<Box xcss={styles.descriptionEditForm}>
								<Flex alignItems="start" gap="space.100">
									<Box xcss={styles.editField}>
										<TextArea
											aria-label={formatMessage(messages.descriptionInputLabel)}
											aria-describedby={spaceDetailsErrorId}
											autoFocus
											maxLength={SPACE_ABOUT_MAX_LENGTH}
											value={draftDescription}
											onChange={(event) => setDraftDescription(event.currentTarget.value)}
											onKeyDown={(event) => {
												if (event.key === 'Escape') {
													cancelEditingSpaceDetails();
												}
											}}
										/>
									</Box>
									<InlineEditActionButtons
										saveLabel={formatMessage(messages.saveDescriptionButton)}
										cancelLabel={formatMessage(messages.cancelDescriptionButton)}
										isSaving={isSavingSpaceDetails}
										onCancel={cancelEditingSpaceDetails}
									/>
								</Flex>
								{spaceDetailsError && (
									<Box xcss={styles.fieldError}>
										<div id={spaceDetailsErrorId} role="alert">
											<Text color="color.text.danger">{spaceDetailsError}</Text>
										</div>
									</Box>
								)}
							</Box>
						</form>
					) : description ? (
						<Box xcss={styles.description}>
							{onSpaceDetailsSave ? (
								<Pressable
									xcss={cx(
										styles.inlineEditReadViewButton,
										styles.descriptionInlineEditReadViewButton,
									)}
									aria-label={formatMessage(messages.editDescriptionLabel)}
									onClick={startEditingDescription}
									onMouseEnter={() => setIsDescriptionEditButtonVisible(true)}
									onMouseLeave={() => setIsDescriptionEditButtonVisible(false)}
									onFocus={() => setIsDescriptionEditButtonVisible(true)}
									onBlur={hideDescriptionEditButtonOnBlur}
									testId="space-description-edit-button"
								>
									<Box xcss={styles.editField}>
										<Text color="color.text.subtle">{description}</Text>
									</Box>
									<Box
										xcss={cx(
											styles.editIcon,
											styles.hiddenEditButton,
											isDescriptionEditButtonVisible && styles.visibleEditButton,
										)}
										aria-hidden="true"
									>
										<PencilIcon label="" />
									</Box>
								</Pressable>
							) : (
								<Text color="color.text.subtle">{description}</Text>
							)}
						</Box>
					) : isDescriptionLoading ? null : onSpaceDetailsSave ? (
						<Box xcss={styles.description}>
							<Button
								appearance="subtle"
								iconBefore={AddIcon}
								onClick={startEditingDescription}
								testId="space-description-add-button"
							>
								{formatMessage(messages.addDescriptionButton)}
							</Button>
						</Box>
					) : null}

					{/* Tab bar */}
					<Flex
						role="tablist"
						aria-label={formatMessage(messages.tabListLabel)}
						xcss={styles.tabBar}
					>
						<TabButton
							isActive={activeTab === 'starters'}
							label={formatMessage(messages.startersTab)}
							onClick={() => setActiveTab('starters')}
							testId="tab-starters"
						/>
						{isSpacesEnabled && (
							<TabButton
								isActive={activeTab === 'chats'}
								label={formatMessage(messages.chatsTab)}
								onClick={() => setActiveTab('chats')}
								testId="tab-chats"
							/>
						)}
						<TabButton
							isActive={activeTab === 'sources'}
							label={formatMessage(messages.sourcesTab)}
							onClick={() => setActiveTab('sources')}
							testId="tab-sources"
						/>
						<TabButton
							isActive={activeTab === 'memory'}
							label={formatMessage(messages.memoryTab)}
							onClick={() => setActiveTab('memory')}
							testId="tab-memory"
						/>
					</Flex>

					{/* Starters tab */}
					{activeTab === 'starters' && starterCardWithSkillTags}

					{/* Chats tab */}
					{isSpacesEnabled && activeTab === 'chats' && (
						<Box role="tabpanel" testId="chats-list">
							{isChatsLoading ? (
								<Box xcss={styles.chatsLoading}>
									<Spinner size="medium" label={formatMessage(messages.loadingChatsLabel)} />
								</Box>
							) : chats.length === 0 ? (
								<Box xcss={styles.emptyChatsCard} testId="empty-chats-card">
									<Box
										as="img"
										src={chatAvatarsUrl}
										alt=""
										aria-hidden="true"
										xcss={styles.emptyChatsIcon}
									/>
									<Box xcss={styles.emptyChatsText}>
										<Heading size="small">{formatMessage(messages.noChatsTitle)}</Heading>
										<Text color="color.text.subtle">
											{formatMessage(messages.noChats, { spaceName: title })}
										</Text>
									</Box>
								</Box>
							) : (
								<Box xcss={styles.chatList}>
									{chats.map((chat) => {
										const chatTitle = chat.name || formatMessage(messages.untitledChat);
										return (
											<ChatItem
												key={chat.id}
												conversation={{ ...chat, name: chatTitle }}
												onConversationSelect={() => onChatClick?.(chat)}
												isSpinnerOverlayConversationIconEnabled={false}
												hideActions
												cloudId={cloudId}
												testId="space-chat-item"
												ariaLabel={formatMessage(messages.openChatLabel, {
													name: chatTitle,
												})}
											/>
										);
									})}
								</Box>
							)}
						</Box>
					)}

					{/* Sources tab */}
					{activeTab === 'sources' && (
						<Box role="tabpanel" testId="sources-list">
							{sources.length === 0 ? (
								<Box xcss={styles.emptySourcesCard} testId="empty-sources-card">
									<EmptySourcesState
										title={formatMessage(messages.noSourcesTitle)}
										description={formatMessage(messages.noSources)}
										headingSize="medium"
										action={
											onSourceAdded && (
												<SourceLinkPickerPopup
													plugins={plugins}
													cloudId={cloudId}
													sources={sources}
													onSourceAdded={handleManualSourceAdded}
													trigger={({ ref, onClick, ...triggerProps }) => (
														<span ref={ref} {...triggerProps}>
															<Button
																appearance="primary"
																onClick={onClick}
																iconBefore={AddIcon}
																testId="add-sources-button"
															>
																{formatMessage(messages.addSourcesButton)}
															</Button>
														</span>
													)}
												/>
											)
										}
									/>
								</Box>
							) : (
								<Flex direction="column" gap="space.200" alignItems="stretch">
									<Box xcss={styles.sourceList}>
										{sources.map((source, index) => (
											<SourceCard
												key={source.id}
												source={source}
												onDelete={handleDeleteSource}
												deleteLabel={formatMessage(messages.deleteSourceLabel, {
													name: source.title,
												})}
												isLast={index === sources.length - 1}
											/>
										))}
									</Box>
									{onSourceAdded && (
										<Flex justifyContent="start">
											<SourceLinkPickerPopup
												plugins={plugins}
												sources={sources}
												cloudId={cloudId}
												onSourceAdded={handleManualSourceAdded}
												trigger={({ ref, onClick, ...triggerProps }) => (
													<span ref={ref} {...triggerProps}>
														<Button
															onClick={onClick}
															iconBefore={AddIcon}
															testId="add-sources-button"
														>
															{formatMessage(messages.addSourcesButton)}
														</Button>
													</span>
												)}
											/>
										</Flex>
									)}
								</Flex>
							)}
						</Box>
					)}

					{activeTab === 'memory' && (
						<Box role="tabpanel" testId="memory-list">
							<Flex direction="column" gap="space.200" alignItems="stretch">
								{memoriesStoreError !== null && (
									<Text color="color.text.danger" testId="memory-fetch-error">
										{formatMessage(messages.loadMemoriesError)}
									</Text>
								)}
								{isMemoriesLoading ? (
									<Box xcss={styles.sourceList} testId="memory-loading-skeleton">
										{[0, 1, 2].map((i) => (
											<Box
												key={i}
												xcss={cx(styles.sourceCard, i < 2 ? undefined : styles.noBorderEnd)}
											>
												<Skeleton width="100%" height="20px" isShimmering />
											</Box>
										))}
									</Box>
								) : !memories.length && !isAddingMemory ? (
									<Box xcss={styles.emptyMemoriesCard}>
										<EmptyMemoriesIcon />
										<Box xcss={styles.emptyMemoriesText}>
											<Heading size="small">{formatMessage(messages.noMemoriesTitle)}</Heading>
											<Text>{formatMessage(messages.noMemories)}</Text>
										</Box>
										<Button
											appearance="primary"
											onClick={openAddMemory}
											iconBefore={AddIcon}
											testId="add-memory-button-empty-state"
										>
											{formatMessage(messages.addMemoryButton)}
										</Button>
									</Box>
								) : (
									<>
										{memoryDeleteErrorSpaceId === spaceId && (
											<Text color="color.text.danger" testId="memory-delete-error">
												{formatMessage(messages.deleteMemoryError)}
											</Text>
										)}
										<Box xcss={styles.sourceList}>
											{memories.map((memory, index, arr) => (
												<MemoryCard
													key={memory.id}
													memory={memory}
													onDelete={handleDeleteMemory}
													deleteLabel={formatMessage(messages.deleteMemoryLabel, {
														text: memory.text,
													})}
													isLast={index === arr.length - 1}
												/>
											))}
										</Box>
										{isAddingMemory ? (
											// Submission is driven by the native <form> rather than a
											// keydown handler on the input. Form submit is dispatched by
											// the browser to whichever input has focus, so it stays
											// reliable across re-opens of the input (a keydown handler bound
											// to a specific node can become stale when the input is
											// collapsed and re-opened).
											<form
												onSubmit={(e) => {
													e.preventDefault();
													void handleSubmitMemory();
												}}
											>
												<Textfield
													autoFocus
													value={newMemoryText}
													isInvalid={memorySubmitError !== null}
													onChange={(e) => {
														setMemorySubmitError(null);
														setNewMemoryText((e.target as HTMLInputElement).value);
													}}
													onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
														if (e.key === 'Escape') {
															setIsAddingMemory(false);
															setNewMemoryText('');
															setMemorySubmitError(null);
														}
													}}
													elemAfterInput={
														<Box paddingInline="space.100" paddingBlock="space.100">
															<Button
																type="submit"
																iconAfter={ReturnIcon}
																appearance="primary"
																isDisabled={isSubmittingMemory}
															>
																{formatMessage(messages.submitNewMemory)}
															</Button>
														</Box>
													}
													testId="new-memory-input"
												/>
											</form>
										) : (
											<span>
												<Button
													onClick={openAddMemory}
													iconBefore={AddIcon}
													testId="add-memory-button"
												>
													{formatMessage(messages.addMemoryButton)}
												</Button>
											</span>
										)}
									</>
								)}
							</Flex>
						</Box>
					)}
				</Box>
			</Box>
			{renderChatInput && (
				<Box xcss={cx(styles.chatInputArea, fullScreen && styles.chatInputAreaFullScreen)}>
					{shouldRenderSmartContextRecommendations && (
						<Box xcss={styles.chatInputInner}>
							{/* ROVOFE-3042: the slot owns both the recommendations hook and its own
							    visual wrapper, so when there are no recommendations the entire subtree
							    (padding included) is removed from the DOM. */}
							<SmartContextRecommendationsBannerSlot
								key={spaceId}
								spaceId={spaceId}
								cloudId={cloudId}
								variant={fullScreen ? 'inline' : 'stacked'}
								onDismissFocusTarget={focusChatInput}
							/>
						</Box>
					)}
					<Box xcss={styles.chatInputInner}>
						<Box ref={chatInputFocusTargetRef}>
							{renderChatInput({
								sources,
								onSubmit: handleSubmit,
								zeroSourcesBannerAction,
							})}
						</Box>
					</Box>
					<Box xcss={styles.chatInputInner}>
						<Box xcss={styles.aiDisclaimer}>
							<Anchor href="https://www.atlassian.com/trust/ai" xcss={styles.aiDisclaimerAnchor}>
								<Inline space="space.050" alignBlock="center" alignInline="center">
									<InformationCircleIcon
										label=""
										color={token('color.icon.subtlest')}
										size="small"
									/>
									<Text size="small" color="color.text.subtle">
										{formatMessage(messages.aiDisclaimer)}
									</Text>
									<LinkExternalIcon label="" color={token('color.icon.subtlest')} size="small" />
								</Inline>
							</Anchor>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export const SpaceLandingPage: React.MemoExoticComponent<
	(props: SpaceLandingPageProps) => React.JSX.Element
> = memo(SpaceLandingPageComponent);
